import AbortController from '@sh/helpers/AbortController';
import TrafficSourceConfig from '@sh/helpers/TrafficSourceConfig';
import { TrafficSource } from '@sh/types';
import { cloneDeep, isArray, isObject } from 'lodash';
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
import { permissionManager } from '@paperclicks/unleash-client';

const timeout = 300;
const pageSize = 60;
const TheOptimizer = 'TheOptimizer';
const LoadingMessage = 'Loading data, please wait...';
const EmptyTableCell = 'N/A';
const unleashFeatures = ['media-manager'];

const canAccess = async (feature: string): Promise<boolean> => {
  const { hasPermission } = await permissionManager.check(feature, {}, true);
  return hasPermission;
};

const roundNumber = (number: number, isPercentage = false) => {
  if (number > 10) {
    return Math.round(number);
  }
  const divider = isPercentage ? 10000 : 100;
  return Math.round((number + Number.EPSILON) * divider) / divider;
};

const deleteEmptyFields = (data: Record<string, any>) => {
  const clonedData = cloneDeep(data);

  for (const key in clonedData) {
    if (
      (!clonedData[key] && !isObject(clonedData[key]))
      || (isObject(clonedData[key])
        && ((isArray(clonedData[key]) && !clonedData[key].length) || !Object.keys(clonedData[key]).length))
    ) {
      delete clonedData[key];
    }
  }
  return clonedData;
};

const addImageEditorScript = () => {
  const imageEditorScriptID = 'image-editor';

  if (!document.getElementById(imageEditorScriptID)) {
    const imageEditorScript = document.createElement('script');
    imageEditorScript.setAttribute('id', imageEditorScriptID);
    imageEditorScript.setAttribute('src', '/sh_static/scripts/filerobot-image-editor-v4.6.3.min.js');
    document.head.appendChild(imageEditorScript);
  }
};

const getConfigData = (trafficSourceName: string) => cloneDeep(TrafficSourceConfig[trafficSourceName]);

const imageToBase64 = (url: string) => fetch(url)
  .then((response) => response.blob())
  .then(
    (blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    })
  );

const textToInsert = (text: string, tags: string[]) => {
  text = text.trim();

  for (const tag of tags) {
    if (text === tag) {
      text = '';
      break;
    }
  }
  return text;
};

const pluralize = (count: number, noun: string, suffix = 's') => `${noun}${count !== 1 ? suffix : ''}`;

const onImageError = (event: Event) => {
  const eventTarget = event.target as HTMLImageElement;

  if (eventTarget && !eventTarget?.dataset.image_error) {
    eventTarget.src = '/sh_static/other/no-image.svg';
  }
};

const isVideoUrl = (data?: string) => {
  const link = data ?? '';

  if (link.includes('mime_type=video') || link.includes('www.youtube.com/embed/')) {
    return true;
  }

  const videoExt = ['mp4', 'webm', 'ogg', 'mov'];
  const url = link.split('.');
  const ext = url[url.length - 1];

  return videoExt.includes(ext);
};

const getLogoUniqueName = (name: TrafficSource) => {
  switch (name) {
    case 'Gemini':
      return 'YahooGemini';
    case 'RevContent':
      return 'Revcontent';
    case 'VoluumDSP':
      return 'VolumDSP';
    default:
      return name;
  }
};

const getTrafficSourceLogo = (name: TrafficSource) => {
  if (process.env.VUE_APP_PLATFORM === 'mobile') {
    return `/sh_static/accounts/${name}.png`;
  }
  return `https://mediamanager-images.theoptimizerimg.com/config/${getLogoUniqueName(name)}.svg`;
}

const getNestedValue = (from: any, selectors: string) => selectors
  .replace(/\[([^\]]*)]/g, '.$1.')
  .split('.')
  .filter((item) => item !== '')
  .reduce((acc, cur) => {
    if (acc instanceof Object) {
      return acc[cur];
    }
    return undefined;
  }, from);

const defaultColumnsWidth = {
  headline: 350,
  traffic_source_unique_name: 180,
  traffic_source_item_id: 300,
  name: 300,
  date_range: 150,
  'additional_fields.daily_budget': 180,
  'additional_fields.budget': 120,
  'additional_fields.bid_strategy': 210
};

export {
  AbortController,
  EmptyTableCell,
  LoadingMessage,
  TheOptimizer,
  addImageEditorScript,
  canAccess,
  defaultColumnsWidth,
  deleteEmptyFields,
  getConfigData,
  getLogoUniqueName,
  getNestedValue,
  getTrafficSourceLogo,
  imageToBase64,
  isVideoUrl,
  onImageError,
  pageSize,
  pluralize,
  roundNumber,
  textToInsert,
  timeout,
  unleashFeatures
};
